import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Save, Loader2, Car } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls } from '../../../components/admin/billing/shared';
import { MessageTemplates } from '../../../components/admin/settings/MessageTemplates';
import { ServicePackages } from '../../../components/admin/appointments/ServicePackages';
import { BookingRules } from '../../../components/admin/appointments/BookingRules';
import { CommonProblemsEditor } from '../../../components/admin/appointments/CommonProblems';
import { WarrantyPageSettings } from '../../../components/admin/billing/WarrantyPageSettings';
import { EmailSettings, EmailWarning } from '../../../components/admin/settings/EmailSettings';
import { PurchaseOrderSettings } from '../../../components/admin/settings/PurchaseOrderSettings';
import { ReferEarn } from '../../../components/admin/settings/ReferEarn';
import { LogoUploader } from '../../../components/admin/billing/LogoUploader';
import { BrandColorPicker, StaffInviteLinks } from '../../../components/admin/billing/BrandingControls';
import { PaymentGatewayCard } from '../../../components/admin/billing/PaymentGatewayCard';
import { ThemeChoice } from '../../../components/admin/ThemeChoice';
import { OnboardingChecklist } from '../../../components/admin/OnboardingChecklist';
import { useTenant } from '../../../contexts/TenantContext';

// India state codes for GST (state code → state name).
// Same canonical list used by the standalone GST settings page.
const STATE_OPTIONS = [
  { code: '29', name: 'Karnataka' },
  { code: '01', name: 'Jammu & Kashmir' },
  { code: '02', name: 'Himachal Pradesh' },
  { code: '03', name: 'Punjab' },
  { code: '04', name: 'Chandigarh' },
  { code: '05', name: 'Uttarakhand' },
  { code: '06', name: 'Haryana' },
  { code: '07', name: 'Delhi' },
  { code: '08', name: 'Rajasthan' },
  { code: '09', name: 'Uttar Pradesh' },
  { code: '10', name: 'Bihar' },
  { code: '11', name: 'Sikkim' },
  { code: '12', name: 'Arunachal Pradesh' },
  { code: '13', name: 'Nagaland' },
  { code: '14', name: 'Manipur' },
  { code: '15', name: 'Mizoram' },
  { code: '16', name: 'Tripura' },
  { code: '17', name: 'Meghalaya' },
  { code: '18', name: 'Assam' },
  { code: '19', name: 'West Bengal' },
  { code: '20', name: 'Jharkhand' },
  { code: '21', name: 'Odisha' },
  { code: '22', name: 'Chhattisgarh' },
  { code: '23', name: 'Madhya Pradesh' },
  { code: '24', name: 'Gujarat' },
  { code: '27', name: 'Maharashtra' },
  { code: '30', name: 'Goa' },
  { code: '32', name: 'Kerala' },
  { code: '33', name: 'Tamil Nadu' },
  { code: '34', name: 'Puducherry' },
  { code: '36', name: 'Telangana' },
  { code: '37', name: 'Andhra Pradesh' },
];

const GROUPS = [
  // Only while there is still something to do — see setupDone below.
  { key: 'setup', label: 'Getting started' },
  { key: 'profile', label: 'Workshop profile' },
  { key: 'branding', label: 'Branding' },
  { key: 'appearance', label: 'Appearance' },
  { key: 'invoicing', label: 'Invoicing' },
  { key: 'messages', label: 'Customer messages' },
  { key: 'email', label: 'Email' },
  { key: 'packages', label: 'Service packages' },
  { key: 'booking', label: 'Booking rules' },
  { key: 'subscription', label: 'Subscription' },
  { key: 'refer', label: 'Refer & Earn' },
];

const SIG_LABEL = 'block text-xs uppercase tracking-wider text-slate-500 mb-1';

const MessageSignature = ({ form, set }) => {
  const preview = [
    form.message_thank_you || 'Thank you for choosing us.',
    `— ${form.company_name || 'Team'}`,
    form.contact_phone && `Call us: ${form.contact_phone}`,
    (form.google_review_url || form.google_place_id) && `\nHappy with our work? Please leave us a Google review: ${form.google_review_url || `https://search.google.com/local/writereview?placeid=${form.google_place_id}`}`,
  ].filter(Boolean).join('\n');
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-4" data-testid="message-signature-section">
      <div>
        <h2 className="text-sm font-semibold text-slate-900">WhatsApp message sign-off</h2>
        <p className="text-xs text-slate-500 mt-0.5">Added to the end of every message you send to customers: estimates, invoices, statements, warranties, appointments and payment reminders. Company name and phone come from above.</p>
      </div>
      <div>
        <label className={SIG_LABEL}>Thank-you line</label>
        <input value={form.message_thank_you} maxLength={200} onChange={(e) => set('message_thank_you', e.target.value)} placeholder="Thank you for choosing us." className={inputCls} data-testid="message-thank-you" />
      </div>
      <div>
        <label className={SIG_LABEL}>Google review link</label>
        <input value={form.google_review_url} onChange={(e) => set('google_review_url', e.target.value)} placeholder="https://g.page/r/your-workshop/review" className={inputCls} data-testid="google-review-url" />
        <div className="text-[10px] text-slate-400 mt-1">Leave blank to build it from the Place ID below.</div>
      </div>
      <div>
        <label className={SIG_LABEL}>Google Maps Place ID</label>
        <input value={form.google_place_id} onChange={(e) => set('google_place_id', e.target.value.trim())} placeholder="ChIJ…" className={inputCls} data-testid="google-place-id" />
        <div className="text-[10px] text-slate-400 mt-1">Shows your live Google rating on the feedback page and dashboard. Find it at developers.google.com/maps/documentation/places/web-service/place-id.</div>
      </div>
      <div>
        <div className={SIG_LABEL}>Preview</div>
        <pre className="whitespace-pre-wrap rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-xs text-slate-700 font-sans" data-testid="message-signature-preview">{preview}</pre>
      </div>
    </section>
  );
};

const BillingSettings = () => {
  const { authHeader } = useAuth();
  // Keeps the sidebar logo in step with an upload or removal.
  const { refresh: refreshTenant, tenant } = useTenant();
  // ?tab=branding lets anything link straight to the right section.
  const [params, setParams] = useSearchParams();
  const [setupDone, setSetupDone] = useState(true);
  const tabs = setupDone ? GROUPS.filter((g) => g.key !== 'setup') : GROUPS;
  const fallback = setupDone ? 'profile' : 'setup';
  const group = tabs.some((g) => g.key === params.get('tab')) ? params.get('tab') : fallback;
  const setGroup = (key) => setParams(key === fallback ? {} : { tab: key }, { replace: true });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [hasLogo, setHasLogo] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/billing/settings`, { headers: authHeader() });
      setForm({
        company_name: data.company_name || '',
        address: data.address || '',
        contact_name: data.contact_name || '',
        contact_phone: data.contact_phone || '',
        contact_email: data.contact_email || '',
        company_gstin: data.company_gstin || '',
        company_website: data.company_website || '',
        warranty_page_url: data.warranty_page_url || '',
        brand_color: data.brand_color || '',
        company_state: data.company_state || 'Karnataka',
        company_state_code: data.company_state_code || '29',
        bank_holder_name: data.bank_holder_name || '',
        bank_name: data.bank_name || '',
        bank_account_number: data.bank_account_number || '',
        bank_ifsc: data.bank_ifsc || '',
        upi: data.upi || '',
        terms_pay: (data.terms_pay || []).join('\n'),
        terms_service: (data.terms_service || []).join('\n'),
        estimate_reminder_lead_days: Number(data.estimate_reminder_lead_days) || 2,
        message_thank_you: data.message_thank_you || '',
        google_review_url: data.google_review_url || '',
        google_place_id: data.google_place_id || '',
      });
      setHasLogo(Boolean(data.has_logo));
    } catch (e) {
      setErr(formatErr(e));
    }
  }, [authHeader]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    setMsg(''); setErr('');
    try {
      const payload = {
        ...form,
        terms_pay: form.terms_pay.split('\n').map((s) => s.trim()).filter(Boolean),
        terms_service: form.terms_service.split('\n').map((s) => s.trim()).filter(Boolean),
        estimate_reminder_lead_days: Number(form.estimate_reminder_lead_days) || 2,
      };
      await axios.put(`${API}/billing/settings`, payload, { headers: authHeader() });
      // Picks up a changed brand colour so the app recolours without a reload.
      refreshTenant();
      setMsg('Settings saved. Existing PDFs will regenerate with the new details on next view.');
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const h = window.location.hash.slice(1);
    if (form && h) setTimeout(() => document.getElementById(h)?.scrollIntoView({ behavior: 'smooth' }), 300);
  }, [form]);


  if (!form) {
    // A 403 here means they do not hold can_manage_billing_settings. Saying so
    // beats a spinner that never resolves.
    if (err) {
      return (
        <div className="min-h-screen px-4 sm:px-6 py-10" data-testid="settings-denied">
          <div className="max-w-md mx-auto bg-white border border-slate-200 rounded-xl p-6 text-center">
            <p className="text-sm font-semibold text-slate-900">Settings are not open to you</p>
            <p className="mt-1.5 text-sm text-slate-500">
              Your workshop owner can give you access to Settings, or change
              anything here for you.
            </p>
            <Link
              to="/admin"
              className="mt-4 inline-block text-xs font-semibold text-teal-700"
              data-testid="settings-denied-home"
            >
              Back to the console
            </Link>
          </div>
        </div>
      );
    }
    return <div className="p-10 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2" />Loading…</div>;
  }

  return (
    <div className="min-h-screen px-4 sm:px-6 py-6">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-slate-900">Settings</h1>
            <p className="text-sm text-slate-500">
              Your workshop's details, branding and invoicing — everything that
              appears on the paperwork you send out.
            </p>
          </div>
          <button onClick={save} disabled={saving} className={`${['messages', 'packages', 'booking', 'email', 'refer'].includes(group) ? 'hidden' : 'inline-flex'} items-center gap-1.5 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white text-sm font-semibold px-4 py-2 rounded-lg whitespace-nowrap`} data-testid="save-settings-btn">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>

        <nav className="flex flex-wrap gap-1.5" aria-label="Settings sections">
          {tabs.map((g) => (
            <button
              key={g.key}
              type="button"
              onClick={() => setGroup(g.key)}
              className={`text-xs font-semibold rounded-full border px-3 py-1.5 transition-colors ${
                group === g.key
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-300 text-slate-600 hover:border-slate-400'
              }`}
              data-testid={`settings-tab-${g.key}`}
            >
              {g.label}
            </button>
          ))}
        </nav>

        {group !== 'email' && <EmailWarning onOpen={() => setGroup('email')} />}
        {group === 'email' && <EmailSettings />}
        {group === 'refer' && <ReferEarn />}
        {group === 'invoicing' && <PurchaseOrderSettings />}
        {msg && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">{msg}</div>}
        {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}


        {['setup', 'profile', 'invoicing'].includes(group) && (
          <Link to="/admin/import" className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl p-5 hover:border-teal-500" data-testid="settings-import-link">
          <span><span className="block text-sm font-semibold text-teal-700 uppercase tracking-wider">Bring your data</span>
            <span className="text-xs text-slate-500">Import customers, vehicles, dues, stock, vendors and past bills from Excel or your old software yourself.</span></span>
          <span className="shrink-0 rounded-lg bg-teal-600 px-3 py-2 text-xs font-semibold text-white">Open import</span>
        </Link>
        )}

        {group === 'branding' && (
        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="branding-section">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">Branding</h2>
            <span className="text-[10px] text-slate-400">Shown on every document you issue</span>
          </div>
          {/* Only the logo flag is refreshed — re-fetching the whole settings
              object here would silently wipe any unsaved edits on this page. */}
          <LogoUploader
            hasLogo={hasLogo}
            company={form.company_name}
            onChange={(next) => { setHasLogo(next); refreshTenant(); }}
          />
          <div className="pt-1">
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">Brand colour</label>
            <BrandColorPicker value={form.brand_color} onChange={(hex) => set('brand_color', hex)} />
          </div>

          <div className="pt-1">
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">Staff sign-in links</label>
            <StaffInviteLinks slug={tenant?.slug} />
          </div>
        </section>
        )}

        {group === 'appearance' && (
        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid="appearance-section">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">Appearance</h2>
            <span className="text-[10px] text-slate-400">Personal to you, not the workshop</span>
          </div>
          <ThemeChoice />
          <p className="text-[11px] text-slate-400">
            Applies to the console and staff portal on every device you sign in on.
            Invoices, estimates and certificates always print light.
          </p>
        </section>
        )}

        {/* Always mounted, so it can report whether the tab is needed at all;
            shown only on its own tab. */}
        <div className={group === 'setup' ? '' : 'hidden'}>
          <OnboardingChecklist onStatus={setSetupDone} />
        </div>

        {group === 'profile' && (
        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="profile-section">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">Workshop profile</h2>
            <span className="text-[10px] text-slate-400">Printed on every document you issue</span>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Company name</label>
            <input value={form.company_name} onChange={(e) => set('company_name', e.target.value)} className={inputCls} data-testid="company-name" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Address (one line per row)</label>
            <textarea rows={4} value={form.address} onChange={(e) => set('address', e.target.value)} className={inputCls} data-testid="company-address" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Contact name</label>
              <input value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} className={inputCls} data-testid="contact-name" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Contact phone</label>
              <input value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} className={inputCls} data-testid="contact-phone" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Contact email</label>
              <input value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} className={inputCls} data-testid="contact-email" />
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Website</label>
            <input
              value={form.company_website}
              onChange={(e) => set('company_website', e.target.value)}
              placeholder="e.g. sharmamotors.in"
              className={inputCls}
              data-testid="company-website"
            />
            <div className="text-[10px] text-slate-400 mt-1">
              Printed in the eWarranty certificate footer and shown on your verification page.
            </div>
          </div>
          <WarrantyPageSettings value={form.warranty_page_url} onChange={(v) => set('warranty_page_url', v)} authHeader={authHeader} />
        </section>
        )}

        {group === 'profile' && <MessageSignature form={form} set={set} />}
        {group === 'messages' && <MessageTemplates />}
        {group === 'packages' && <ServicePackages />}
        {group === 'booking' && <><BookingRules /><CommonProblemsEditor /></>}

        {group === 'profile' && (
        <section className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-teal-50 text-teal-600"><Car size={18} /></div>
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Vehicle Catalog</h2>
              <p className="text-xs text-slate-500 mt-0.5">Manage custom makes &amp; models that appear in the customer form dropdowns.</p>
            </div>
          </div>
          <Link
            to="/admin/billing/vehicle-catalog"
            className="text-sm font-medium text-teal-600 hover:text-teal-700 whitespace-nowrap"
            data-testid="open-vehicle-catalog"
          >
            Manage →
          </Link>
        </section>
        )}

        {group === 'invoicing' && (
        <>
        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="gst-section">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">GST Registration</h2>
            <span className="text-[10px] text-slate-400">Used on GST invoices &amp; auto-synced to GST Setup</span>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">GSTIN (15 chars)</label>
            <input
              value={form.company_gstin}
              onChange={(e) => set('company_gstin', e.target.value.toUpperCase())}
              placeholder="e.g. 29ABCDE1234F1Z5"
              maxLength={15}
              className={`${inputCls} font-mono tracking-wider`}
              data-testid="company-gstin"
            />
            <div className="text-[10px] text-slate-400 mt-1">Leave blank if not GST-registered. Format: 2 digit state code + PAN + entity code + Z + checksum.</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">State (Place of Supply default)</label>
              <select
                value={form.company_state_code}
                onChange={(e) => {
                  const code = e.target.value;
                  const name = STATE_OPTIONS.find((s) => s.code === code)?.name || '';
                  setForm((s) => ({ ...s, company_state_code: code, company_state: name }));
                }}
                className={inputCls}
                data-testid="company-state-code"
              >
                {STATE_OPTIONS.map((s) => (
                  <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">State name</label>
              <input
                value={form.company_state}
                onChange={(e) => set('company_state', e.target.value)}
                className={inputCls}
                data-testid="company-state"
              />
            </div>
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">Bank &amp; UPI</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Account holder name</label>
              <input value={form.bank_holder_name} onChange={(e) => set('bank_holder_name', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Bank name</label>
              <input value={form.bank_name} onChange={(e) => set('bank_name', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Account number</label>
              <input value={form.bank_account_number} onChange={(e) => set('bank_account_number', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">IFSC code</label>
              <input value={form.bank_ifsc} onChange={(e) => set('bank_ifsc', e.target.value)} className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">UPI (Google Pay / PhonePe / Paytm)</label>
              <input value={form.upi} onChange={(e) => set('upi', e.target.value)} className={inputCls} data-testid="billing-upi" />
              <div className="text-[10px] text-slate-400 mt-1">
                Printed as a line of text on your documents. For a scannable QR and a
                Pay now link on invoices, set up Payments below.
              </div>
            </div>
          </div>
        </section>

        <PaymentGatewayCard />


        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">Terms &amp; Conditions</h2>          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Payment terms (one per line)</label>
            <textarea rows={4} value={form.terms_pay} onChange={(e) => set('terms_pay', e.target.value)} className={inputCls} data-testid="terms-pay" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Service terms (one per line) — printed on page 3 of the PDF</label>
            <textarea rows={10} value={form.terms_service} onChange={(e) => set('terms_service', e.target.value)} className={inputCls} data-testid="terms-service" />
          </div>
        </section>
        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="reminder-settings">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">Estimate Reminders</h2>
            <span className="text-[10px] text-slate-400">Nudge customers before their estimate expires</span>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Reminder lead time</label>
            <select
              value={form.estimate_reminder_lead_days}
              onChange={(e) => set('estimate_reminder_lead_days', Number(e.target.value))}
              className={inputCls}
              data-testid="reminder-lead-days"
            >
              <option value={2}>2 days before expiry</option>
              <option value={3}>3 days before expiry</option>
              <option value={5}>5 days before expiry</option>
            </select>
            <div className="text-[10px] text-slate-500 mt-1">
              Runs every day at 09:00 IST. Emails the estimate PDF to each customer whose validity date is exactly this many days out (draft/sent only).
            </div>
          </div>
        </section>
        </>
        )}

        {group === 'subscription' && (
        <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid="subscription-section">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold text-teal-700 uppercase tracking-wider">Subscription</h2>
            <span className="text-[10px] text-slate-400">Plan, billing cycle and invoices</span>
          </div>
          <p className="mt-3 text-sm text-slate-500">
            Your plan, what it includes, and the modules you could add live on the
            plan screen.
          </p>
          <Link
            to="/admin/subscription"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-600 hover:text-teal-700"
            data-testid="open-subscription"
          >
            Open Plan &amp; Billing →
          </Link>
        </section>
        )}
      </div>
    </div>
  );
};

export default BillingSettings;
